import React from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { PlusCircle, Hash } from 'lucide-react';
import ImageUploader from '../../../components/ImageUploader';

export function ProductFormDialog({
  open,
  onOpenChange,
  editingProduct,
  form,
  setForm,
  onSave,
  categories,
  brands,
  suppliers,
  onNewBrand,
  onNewCategory,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">
            {editingProduct ? 'Edit Product' : 'Add Product'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label>SKU</Label>
              <Input
                value={form.sku}
                onChange={(e) => setForm({ ...form, sku: e.target.value })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="flex items-center justify-between">
                Brand
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-[#78BE20] hover:text-[#65a31b]"
                  onClick={onNewBrand}
                >
                  <PlusCircle className="h-3 w-3 mr-1" />
                  Add New
                </Button>
              </Label>
              <Select value={form.brand || ''} onValueChange={(v) => setForm({ ...form, brand: v })}>
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700" data-testid="brand-select">
                  <SelectValue placeholder="Select brand" />
                </SelectTrigger>
                <SelectContent>
                  {brands.map(brand => (
                    <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="flex items-center justify-between">
                Category
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-[#78BE20] hover:text-[#65a31b]"
                  onClick={onNewCategory}
                >
                  <PlusCircle className="h-3 w-3 mr-1" />
                  Add New
                </Button>
              </Label>
              <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700" data-testid="category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Supplier</Label>
            <Select value={form.supplier_id || 'none'} onValueChange={(v) => setForm({ ...form, supplier_id: v === 'none' ? '' : v })}>
              <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700" data-testid="supplier-select">
                <SelectValue placeholder="Select supplier (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No supplier</SelectItem>
                {suppliers.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-1 bg-zinc-800 border-zinc-700"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Retail Price</Label>
              <Input
                type="number"
                step="0.01"
                value={form.pricing?.retail || 0}
                onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, retail: parseFloat(e.target.value) } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label>Trade Price</Label>
              <Input
                type="number"
                step="0.01"
                value={form.pricing?.trade || 0}
                onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, trade: parseFloat(e.target.value) } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Stock Quantity</Label>
              <Input
                type="number"
                value={form.stock_qty}
                onChange={(e) => setForm({ ...form, stock_qty: parseInt(e.target.value) || 0 })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label>Low Stock Threshold</Label>
              <Input
                type="number"
                value={form.low_stock_threshold ?? 5}
                onChange={(e) => setForm({ ...form, low_stock_threshold: parseInt(e.target.value) || 0 })}
                className="mt-1 bg-zinc-800 border-zinc-700"
                placeholder="5"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Customer Min Qty</Label>
              <Input
                type="number"
                min="1"
                value={form.min_order_qty ?? 1}
                onChange={(e) => setForm({ ...form, min_order_qty: parseInt(e.target.value) || 1 })}
                className="mt-1 bg-zinc-800 border-zinc-700"
                placeholder="1"
                data-testid="min-order-qty"
              />
              <p className="text-zinc-500 text-xs mt-1">Min qty a customer can order</p>
            </div>
            <div>
              <Label>Customer Max Qty <span className="text-zinc-500 text-xs">(blank = unlimited)</span></Label>
              <Input
                type="number"
                min="1"
                value={form.max_order_qty || ''}
                onChange={(e) => setForm({ ...form, max_order_qty: e.target.value ? parseInt(e.target.value) : null })}
                className="mt-1 bg-zinc-800 border-zinc-700"
                placeholder="No limit"
                data-testid="max-order-qty"
              />
              <p className="text-zinc-500 text-xs mt-1">Max qty a customer can order</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Supplier MOQ</Label>
              <Input
                type="number"
                min="1"
                value={form.supplier_moq || ''}
                onChange={(e) => setForm({ ...form, supplier_moq: e.target.value ? parseInt(e.target.value) : null })}
                className="mt-1 bg-zinc-800 border-zinc-700"
                placeholder="1"
                data-testid="supplier-moq"
              />
              <p className="text-zinc-500 text-xs mt-1">Min qty supplier will sell</p>
            </div>
            <div>
              <Label>Reorder Qty</Label>
              <Input
                type="number"
                min="1"
                value={form.reorder_qty || ''}
                onChange={(e) => setForm({ ...form, reorder_qty: e.target.value ? parseInt(e.target.value) : null })}
                className="mt-1 bg-zinc-800 border-zinc-700"
                placeholder="Default PO qty"
                data-testid="reorder-qty"
              />
              <p className="text-zinc-500 text-xs mt-1">Default qty when creating a PO</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 bg-zinc-800 rounded-lg border border-zinc-700">
            <input
              type="checkbox"
              checked={form.track_serials || false}
              onChange={(e) => setForm({ ...form, track_serials: e.target.checked })}
              className="w-4 h-4 rounded border-zinc-600 accent-[#78BE20]"
              id="track-serials"
              data-testid="track-serials-toggle"
            />
            <label htmlFor="track-serials" className="flex items-center gap-2 cursor-pointer">
              <Hash className="h-4 w-4 text-[#78BE20]" />
              <div>
                <p className="text-white text-sm font-medium">Track Serial Numbers</p>
                <p className="text-zinc-500 text-xs">Require serial numbers when receiving stock via purchase orders</p>
              </div>
            </label>
          </div>

          <div className="flex items-center gap-3 p-3 bg-zinc-800 rounded-lg border border-zinc-700">
            <input
              type="checkbox"
              checked={form.allow_backorder || false}
              onChange={(e) => setForm({ ...form, allow_backorder: e.target.checked })}
              className="w-4 h-4 rounded border-zinc-600 accent-[#78BE20]"
              id="allow-backorder"
              data-testid="allow-backorder-toggle"
            />
            <label htmlFor="allow-backorder" className="flex items-center gap-2 cursor-pointer">
              <svg className="h-4 w-4 text-[#78BE20]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-white text-sm font-medium">Allow Backorders</p>
                <p className="text-zinc-500 text-xs">Allow customers to order when out of stock (fulfilled when stock arrives)</p>
              </div>
            </label>
          </div>

          {/* Shipping Dimensions */}
          <div>
            <Label className="mb-2 block text-zinc-400 text-xs uppercase tracking-wide">Shipping Dimensions</Label>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Weight (kg)</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  value={form.weight_kg || ''}
                  onChange={(e) => setForm({ ...form, weight_kg: e.target.value ? parseFloat(e.target.value) : '' })}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  placeholder="0.5"
                  data-testid="weight-kg"
                />
              </div>
              <div>
                <Label className="text-xs">Length (cm)</Label>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={form.length_cm || ''}
                  onChange={(e) => setForm({ ...form, length_cm: e.target.value ? parseFloat(e.target.value) : '' })}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  placeholder="20"
                  data-testid="length-cm"
                />
              </div>
              <div>
                <Label className="text-xs">Width (cm)</Label>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={form.width_cm || ''}
                  onChange={(e) => setForm({ ...form, width_cm: e.target.value ? parseFloat(e.target.value) : '' })}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  placeholder="15"
                  data-testid="width-cm"
                />
              </div>
              <div>
                <Label className="text-xs">Height (cm)</Label>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={form.height_cm || ''}
                  onChange={(e) => setForm({ ...form, height_cm: e.target.value ? parseFloat(e.target.value) : '' })}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  placeholder="10"
                  data-testid="height-cm"
                />
              </div>
            </div>
            <p className="text-zinc-500 text-xs mt-1">Used for live shipping rate calculations. Defaults: 0.5kg, 20x15x10cm</p>
          </div>

          <div>
            <Label className="mb-3 block">Product Images</Label>
            <ImageUploader
              images={form.images || []}
              onChange={(images) => setForm({ ...form, images })}
              maxImages={8}
            />
          </div>

          <Button onClick={onSave} className="w-full bg-[#78BE20] text-black hover:bg-[#65a31b]">
            {editingProduct ? 'Update Product' : 'Create Product'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
